import { useRouter } from '@tanstack/react-router'
import { AlertTriangle, Bell, CircleAlert, Info } from 'lucide-react'
import { formatRelative } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { AppNotification } from '@/types/api'
import { notificationPath } from '../lib'

const ICONS = { info: Info, warning: AlertTriangle, error: CircleAlert, critical: CircleAlert } as const

/** One inbox row: opens the deep link (when any) and marks the notification read. */
export function NotificationItem({ notification: n, onOpen, compact = false }: { notification: AppNotification; onOpen: (n: AppNotification) => void; compact?: boolean }) {
  const router = useRouter()
  const path = notificationPath(n.url, n.type)
  const Icon = ICONS[n.severity] ?? Bell
  return (
    <button
      type="button"
      onClick={() => {
        onOpen(n)
        if (path) router.history.push(path)
      }}
      className={cn(
        'flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left transition-colors outline-none hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring',
        n.read_at === null && 'bg-primary/5',
      )}
    >
      <Icon
        className={cn(
          'mt-0.5 size-4 shrink-0',
          n.severity === 'warning' && 'text-warning',
          (n.severity === 'error' || n.severity === 'critical') && 'text-danger',
          n.severity === 'info' && 'text-muted-foreground',
        )}
        aria-hidden
      />
      <span className="min-w-0 flex-1">
        <span className={cn('block text-[13px] leading-snug', n.read_at === null ? 'font-semibold' : 'font-medium')}>{n.title}</span>
        {!compact && n.body && <span className="mt-0.5 block text-[12.5px] text-muted-foreground">{n.body}</span>}
        <span className="mt-0.5 block text-[11.5px] text-muted-foreground">{formatRelative(n.created_at)}</span>
      </span>
      {n.read_at === null && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" aria-label="Unread" />}
    </button>
  )
}
